import { type TFunction } from 'i18next'
import { isBookCreditsCoupon } from '../../pages/GiftCard/utils'
import type { GiftCardBalance } from '../../pages/GiftCard/GiftCard.types'
import { getSizeName } from '../../services/SizesManager/SizesManagerHelper'
import { WELCOME_COUPON } from '../../stores/promoCodeStore/promoCode.consts'
import { formatPriceWithPrecision } from '../../utils/utils'
import { type DiscountInfo } from './hooks/useDiscountInfo'
import type {
  DiscountCoupon,
  FreeItemCreditCoupon,
  PromoEntry,
} from './promoEntries.types'

export function createGiftCardEntry(
  balance: GiftCardBalance,
  currency: string | undefined,
  t: TFunction
): PromoEntry {
  const id = `gift_card:${balance.code}`
  const { initialValue, balance: remaining } = balance

  if (initialValue == null || remaining == null || !currency) {
    return { id, type: 'gift_card', code: balance.code, appliance: null }
  }

  return {
    id,
    type: 'gift_card',
    code: balance.code,
    appliance: {
      state: 'applied',
      text: t('general.promo_code_selector.gift_card_description', {
        v1: formatPriceWithPrecision(initialValue, currency),
        v2: formatPriceWithPrecision(remaining, currency),
      }),
    },
  }
}

export function createFreeItemCreditEntry(
  coupon: FreeItemCreditCoupon,
  t: TFunction,
  pricingCountry: string
): PromoEntry {
  const sizes = coupon.metadata?.tileSizesList ?? []
  const sizeLabel =
    sizes.length === 1 ? getSizeName({ size: sizes[0], t, pricingCountry }) : ''
  const key = isBookCreditsCoupon({ metadata: { tileSizesList: sizes } })
    ? 'general.promo_code_selector.free_book_description'
    : 'general.promo_code_selector.free_tile_description'
  return {
    id: `free_item_credit:${coupon.sourceCoupon}`,
    type: 'free_item_credit',
    code: coupon.sourceCoupon,
    appliance: {
      state: 'applied',
      text: t(key, { v1: coupon.amount, v2: sizeLabel }),
    },
  }
}

export function createDiscountEntry(
  coupon: DiscountCoupon | null,
  discountInfo: DiscountInfo | null,
  isLoading: boolean
): PromoEntry {
  const code = coupon ? coupon.pseudonym || coupon.code || WELCOME_COUPON : null

  const appliance: PromoEntry['appliance'] = discountInfo
    ? discountInfo.applied
      ? { state: 'applied', text: discountInfo.appliedText }
      : { state: 'not_met', text: discountInfo.notMetText }
    : isLoading
    ? { state: 'loading' }
    : null

  // The discount entry is singular, so its id is constant — switching between a
  // coupon and the volume discount updates the row in place instead of
  // remounting it (which would make the UI jump).
  return {
    id: 'discount',
    type: 'discount',
    code,
    appliance,
  }
}
